import React, { createContext, useContext, useEffect, useState } from 'react';

interface UiSettings {
  enhancedFooter: boolean;
  sidebarCollapsed: boolean;
  knowledgeOpen: boolean;
  governanceOpen: boolean;
  strategyOpen: boolean;
  dataStewardOpen: boolean;
  administrationOpen: boolean;
}

interface UiSettingsContextValue {
  settings: UiSettings;
  setEnhancedFooter: (value: boolean) => void;
  setSidebarCollapsed: (value: boolean) => void;
  setKnowledgeOpen: (value: boolean) => void;
  setGovernanceOpen: (value: boolean) => void;
  setStrategyOpen: (value: boolean) => void;
  setDataStewardOpen: (value: boolean) => void;
  setAdministrationOpen: (value: boolean) => void;
}

const defaultSettings: UiSettings = {
  enhancedFooter: true,
  sidebarCollapsed: false,
  knowledgeOpen: false,
  governanceOpen: false,
  strategyOpen: false,
  dataStewardOpen: false,
  administrationOpen: false,
};

const UiSettingsContext = createContext<UiSettingsContextValue | undefined>(undefined);

const STORAGE_KEY = 'eunify_ui_settings_v1';

// Load settings synchronously from localStorage on initialization
const loadInitialSettings = (): UiSettings => {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<UiSettings>;
      return { ...defaultSettings, ...parsed };
    }
  } catch (error) {
    console.error('Failed to load UI settings from localStorage', error);
  }
  return defaultSettings;
};

export const UiSettingsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [settings, setSettings] = useState<UiSettings>(loadInitialSettings);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
    } catch (error) {
      console.error('Failed to save UI settings to localStorage', error);
    }
  }, [settings]);

  const setEnhancedFooter = (value: boolean) => {
    setSettings((prev) => ({ ...prev, enhancedFooter: value }));
  };

  const setSidebarCollapsed = (value: boolean) => {
    setSettings((prev) => ({ ...prev, sidebarCollapsed: value }));
  };

  const setKnowledgeOpen = (value: boolean) => {
    setSettings((prev) => ({ ...prev, knowledgeOpen: value }));
  };

  const setGovernanceOpen = (value: boolean) => {
    setSettings((prev) => ({ ...prev, governanceOpen: value }));
  };

  const setStrategyOpen = (value: boolean) => {
    setSettings((prev) => ({ ...prev, strategyOpen: value }));
  };

  const setDataStewardOpen = (value: boolean) => {
    setSettings((prev) => ({ ...prev, dataStewardOpen: value }));
  };

  const setAdministrationOpen = (value: boolean) => {
    setSettings((prev) => ({ ...prev, administrationOpen: value }));
  };

  return (
    <UiSettingsContext.Provider
      value={{
        settings,
        setEnhancedFooter,
        setSidebarCollapsed,
        setKnowledgeOpen,
        setGovernanceOpen,
        setStrategyOpen,
        setDataStewardOpen,
        setAdministrationOpen,
      }}
    >
      {children}
    </UiSettingsContext.Provider>
  );
};

export const useUiSettings = (): UiSettingsContextValue => {
  const context = useContext(UiSettingsContext);
  if (!context) {
    throw new Error('useUiSettings must be used within a UiSettingsProvider');
  }
  return context;
};
